import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, fmtCurrency, fmtDate } from "@/lib/api";
import { Receipt, ExternalLink } from "lucide-react";

const STATUS_STYLES = {
  unpaid: "border-rose-200 bg-rose-50 text-rose-700",
  partial: "border-amber-200 bg-amber-50 text-amber-800",
  paid: "border-emerald-200 bg-emerald-50 text-emerald-700",
};

const STATUSES = ["all", "unpaid", "partial", "paid"];

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  // Sync `status` with the `?status=` URL param so dashboard drill-through
  // links land users pre-filtered. Falls back to "all" when unset/invalid.
  const [searchParams, setSearchParams] = useSearchParams();
  const status = STATUSES.includes(searchParams.get("status") || "") ? searchParams.get("status") : "all";
  const setStatus = (next) => {
    if (next === "all") {
      searchParams.delete("status");
      setSearchParams(searchParams, { replace: true });
    } else {
      setSearchParams({ status: next }, { replace: true });
    }
  };

  useEffect(() => {
    const params = status === "all" ? {} : { status };
    api.get("/invoices", { params }).then(({ data }) => setInvoices(data));
  }, [status]);

  return (
    <div className="space-y-6" data-testid="invoices-root">
      <div className="flex items-end justify-between border-b border-zinc-200 pb-6">
        <div>
          <div className="label-eyebrow">Financial</div>
          <h1 className="text-4xl font-bold tracking-tight text-zinc-950">Invoices</h1>
          <p className="text-sm text-zinc-500 mt-2">Generated from approved orders or direct billing. Track payment status and download.</p>
        </div>
      </div>

      <div className="flex gap-2">
        {STATUSES.map((s) => (
          <button key={s} data-testid={`invoices-filter-${s}`} onClick={() => setStatus(s)} className={`px-3 py-1.5 text-xs uppercase tracking-wider border ${status === s ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}>{s}</button>
        ))}
      </div>

      <div className="brutalist-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 text-zinc-500 uppercase tracking-wider text-[11px] border-b border-zinc-200">
              <th className="text-left px-5 py-3 font-semibold">Invoice #</th>
              <th className="text-left px-5 py-3 font-semibold">Date</th>
              <th className="text-left px-5 py-3 font-semibold">Customer</th>
              <th className="text-right px-5 py-3 font-semibold">Total</th>
              <th className="text-right px-5 py-3 font-semibold">Paid</th>
              <th className="text-left px-5 py-3 font-semibold">Status</th>
              <th className="text-right px-5 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {invoices.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-sm text-zinc-500"><Receipt className="w-6 h-6 mx-auto mb-2 text-zinc-300" />No invoices.</td></tr>
            )}
            {invoices.map((inv) => (
              <tr key={inv.id} className="border-t border-zinc-200 hover:bg-zinc-50" data-testid={`invoice-row-${inv.number}`}>
                <td className="px-5 py-3 font-mono text-zinc-950 font-medium">{inv.number}</td>
                <td className="px-5 py-3 font-mono text-xs text-zinc-500">{fmtDate(inv.created_at)}</td>
                <td className="px-5 py-3 text-zinc-700">{inv.customer_name}</td>
                <td className="px-5 py-3 text-right font-mono font-semibold">{fmtCurrency(inv.total)}</td>
                <td className="px-5 py-3 text-right font-mono text-zinc-700">{fmtCurrency(inv.amount_paid)}</td>
                <td className="px-5 py-3">
                  <span className={`text-[11px] uppercase tracking-wider px-2 py-1 border ${STATUS_STYLES[inv.status]}`}>{inv.status}</span>
                </td>
                <td className="px-5 py-3 text-right">
                  <Link to={`/invoices/${inv.id}`} data-testid={`invoice-view-${inv.number}`} className="text-xs uppercase tracking-wider px-2 py-1 border border-zinc-300 hover:bg-zinc-100 inline-flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Open
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
